import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { TelemetryPanelConfig, TelemetryUnitSystem, TelemetryViewportState } from '../types/telemetry-dashboard.types';
import { isTelemetryChartViz, getTelemetryVizLabel, getTelemetrySubColor } from '../utils/telemetry-color.utils';
import { optionFor } from '../utils/telemetry-chart-options.utils';
import '../styles/telemetry-dashboard-panel.css';

export interface TelemetryDashboardModalProps {
  panel: TelemetryPanelConfig | null;
  timeframe?: number;
  precision?: number;
  units?: TelemetryUnitSystem;
  viewport?: TelemetryViewportState;
  onClose: () => void;
}

export const TelemetryDashboardModal: React.FC<TelemetryDashboardModalProps> = ({
  panel,
  timeframe = 5,
  precision = 2,
  units = {},
  viewport = { spanMs: 300000, offsetMs: 0 },
  onClose,
}) => {
  const chartWrapRef = useRef<HTMLDivElement>(null);
  const ecRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!panel || !isTelemetryChartViz(panel.graphMode)) return;

    if (ecRef.current) {
      ecRef.current.dispose();
      ecRef.current = null;
    }

    const timer = setTimeout(() => {
      const node = chartWrapRef.current;
      if (!node || typeof echarts === 'undefined') return;
      const ec = echarts.init(node, undefined, { renderer: 'canvas' });
      const opt = optionFor(panel as any, timeframe, precision, units as any, viewport);
      ec.setOption(opt);
      ec.resize();
      ecRef.current = ec;
    }, 50);

    return () => {
      clearTimeout(timer);
      if (ecRef.current) {
        ecRef.current.dispose();
        ecRef.current = null;
      }
    };
  }, [panel, timeframe, precision, units, viewport]);

  if (!panel) return null;

  const first = panel.parameters?.[0];
  const subColor = first ? getTelemetrySubColor(first.subsystem) : getTelemetrySubColor('');

  return (
    <div id="telemetry-maximized-modal" className="telemetry-modal-overlay" onClick={onClose}>
      <div id="telemetry-maximized-modal-container" className="telemetry-modal-container" onClick={(e) => e.stopPropagation()}>
        <div id="telemetry-maximized-modal-header" className="telemetry-modal-header">
          {/* <span className="telemetry-panel-sub-dot" style={{ background: subColor }} /> */}
          <span className="telemetry-modal-title">{panel.name || 'Maximized Panel'}</span>
          <span className="telemetry-modal-subtitle">{getTelemetryVizLabel(panel.graphMode)}</span>
          <span className="telemetry-panel-header-spacer" />
          <button id="telemetry-maximized-modal-close-btn" className="telemetry-panel-action-btn" onClick={onClose}>✕</button>
        </div>
        <div className="telemetry-modal-body">
          <div id="telemetry-maximized-modal-chart" ref={chartWrapRef} className="telemetry-panel-chart-canvas" />
        </div>
      </div>
    </div>
  );
};
